import type { ReactNode } from 'react';
import type { FieldError } from '../../core/validation/fieldError';
import { fieldErrorMessage } from './errorPresentation';
import { AlertIcon } from './icons';

/**
 * Campo de formulario con su error debajo.
 *
 * El mensaje se asocia con `aria-describedby` y el campo se marca con `aria-invalid`, de modo
 * que un lector de pantalla anuncia el error al llegar al campo en lugar de dejar al usuario
 * adivinando por qué no avanza el formulario.
 */
export function Field({
  id,
  label,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  error?: FieldError;
  hint?: ReactNode;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
}) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-invalid': !!error, 'aria-describedby': describedBy })}
      {error ? (
        <p className="field__error" id={`${id}-error`}>
          <AlertIcon size={15} />
          {fieldErrorMessage(error)}
        </p>
      ) : (
        hint && (
          <p className="field__hint" id={`${id}-hint`}>
            {hint}
          </p>
        )
      )}
    </div>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  hint,
  type = 'text',
  autoComplete,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: FieldError;
  hint?: ReactNode;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      {(props) => (
        <input
          {...props}
          className="field__control"
          type={type}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

export function TextArea({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: FieldError;
  hint?: ReactNode;
  maxLength?: number;
}) {
  return (
    <Field id={id} label={label} error={error} hint={hint}>
      {(props) => (
        <textarea
          {...props}
          className="field__control"
          value={value}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}
